import * as React from "react"

import { GlobalState } from "../../../store"
import { useAppSelector } from "../../../hooks"

import { Symbols } from "@tenforce/toolbox-fontmap"
import {
    GroupOfSelectable,
    ViewSettingsComponent,
    SelectorFieldProps,
    SelectorHelpers,
} from "@tenforce/product-fields"
import { SelectorLabels } from "@tenforce/product-fields/selectors/types"
import { getColumnsForGroup, getColumnGroups } from "../../../apis/thumbtable"

import { thumbtableAPI } from "../../../apis"
import { Labels } from "../../../util/Translations"
import { formatString } from "../../../util/Strings"

interface Props {
    collectionId: number
}
const PAGE_SIZE = 10

export const ViewSettings = (props: Props): JSX.Element => {
    const [selectableGroup, setSelectableGroup] =
        React.useState<GroupOfSelectable | null>(null)
    // selectedGroup is used to display selected values
    // after toggle it holds the dirty state
    // after onSave, it is synced with the server again
    const [selectedGroup, setSelectedGroup] =
        React.useState<GroupOfSelectable | null>(null)
    const { isLoading: globalIsLoading } = useAppSelector(
        (state: GlobalState) => state.fields,
    )
    const [isLoading, setIsLoading] = React.useState(false)
    const [isSelectedShown, setIsSelectedShown] = React.useState(false)
    const [filter, setFilter] = React.useState("")

    const [selectedGroupIds, setSelectedGroupIds] = React.useState<number[]>([])
    const [selectedSelectableIds, setSelectedSelectableIds] = React.useState<
        number[]
    >([])

    const [totalChildrenCount, setTotalChildrenCount] = React.useState(0)
    const [filteredChildrenCount, setFilteredChildrenCount] = React.useState(0)
    const fieldOptions = {
        title: "",
        isLoading: isLoading || globalIsLoading,
        description: "",
    } as SelectorFieldProps

    React.useEffect(() => {
        thumbtableAPI
            .getTableDataAsSelectables(props.collectionId)
            .then((response: GroupOfSelectable | null) => {
                if (response === null) {
                    return
                }

                let selectedIds: number[] = []

                response.children?.forEach((child) => {
                    const idsToAdd = child.children?.map(
                        (selected) => selected.id,
                    )
                    if (idsToAdd && idsToAdd?.length > 0) {
                        selectedIds = selectedIds.concat(idsToAdd)
                    }
                })
                setSelectedSelectableIds([...selectedIds])
                setFilteredChildrenCount(selectedIds.length)
                setSelectableGroup(response)
                setSelectedGroup(response)
            })
    }, [])

    const getLoadMoreData = async (
        group: GroupOfSelectable,
        filter: string,
        skip: null | number = null,
        top = PAGE_SIZE,
    ): Promise<GroupOfSelectable | null> => {
        skip = skip ?? group.children?.length ?? 0
        let result: GroupOfSelectable[] = []

        result = (await getColumnsForGroup(group.id, filter, skip, top))
            .children

        const loadedChildren = !group.children
            ? result
            : group.children.concat(result)
        const newGroup = { ...group, children: loadedChildren }
        const newSelectableGroup = SelectorHelpers.replaceInnerGroup(
            selectableGroup,
            group.id,
            newGroup,
            selectedGroupIds.concat(selectedSelectableIds),
        )
        setSelectableGroup(newSelectableGroup)
        return newGroup
    }

    const getLevelOneValues = async (
        filter = "",
    ): Promise<GroupOfSelectable | null> => {
        let newTotalChildrenCount = 0
        let result: GroupOfSelectable | null = null

        result = await getColumnGroups(filter)
        newTotalChildrenCount =
            result?.children
                ?.map((child) => child.totalChildrenCount)
                .reduce((a, b) => a + b, 0) ?? 0

        if (!filter) {
            setTotalChildrenCount(newTotalChildrenCount ?? 0)
        }

        return result
    }

    const getFiltered = async (newFilter = ""): Promise<void> => {
        const result = await getLevelOneValues(newFilter)
        setFilter(newFilter.toLowerCase())

        setSelectableGroup(result)
    }

    const getAllValuesInGroup = async (
        groupId: number,
    ): Promise<GroupOfSelectable[]> => {
        return (
            SelectorHelpers.findInnerGroupById(groupId, selectableGroup)
                ?.children ?? []
        )
    }

    const loadChildrenDataForGroup = async (
        group: GroupOfSelectable,
        filter: string,
    ): Promise<void> => {
        const result = await getColumnsForGroup(group.id, filter, 0, PAGE_SIZE)
        const loadedChildren = group.children
            ? group.children?.concat(result.children)
            : result.children
        const newGroup = { ...group, children: loadedChildren }
        const index = selectableGroup?.children?.indexOf(group) ?? -1
        if (index > -1) {
            const updatedChildren = selectableGroup?.children
            updatedChildren?.splice(index, 1, newGroup)
            setSelectableGroup({
                ...selectableGroup,
                children: updatedChildren,
            } as GroupOfSelectable)
        }
    }

    const onModalOpen = async (): Promise<void> => {
        const result = await getLevelOneValues("")

        let newSelectedGroupsIds: number[] = []
        result?.children?.forEach((child) => {
            const childGroup = selectedGroup?.children?.find(
                (node) => child.id === node.id,
            )
            if (
                childGroup != null &&
                childGroup.selectedChildrenCount === child.totalChildrenCount
            ) {
                newSelectedGroupsIds = newSelectedGroupsIds.concat(child.id)
            }
        })

        setSelectedGroupIds(newSelectedGroupsIds)
        setSelectableGroup(result)
    }

    const {
        ClickHereToEdit,
        LoadNMore,
        LoadNMoreOf,
        NothingFound,
        FilterAllNValues,
        FilterNSelectedValues,
        ShowSelectedValues,
        ShowAllValues,
    } = Labels

    const selectorLabels = {
        EmptyPlaceholder: ClickHereToEdit,
        FilterAllNValues: (count) =>
            Symbols.Search + formatString(FilterAllNValues, count),
        FilterNSelectedValues: (count) =>
            Symbols.Search + formatString(FilterNSelectedValues, count),
        FilterNoResults: NothingFound,
        LoadNMore: (remaining) => formatString(LoadNMore, remaining),
        LoadNMoreOf: (pageSize, remaining) =>
            formatString(LoadNMoreOf, pageSize, remaining),
        ShowAllValuesTooltip: ShowAllValues,
        ShowSelectedValuesTooltip: ShowSelectedValues,
    } as SelectorLabels

    const filteredSelectedGroup = React.useMemo(() => {
        if (filter === "") {
            setFilteredChildrenCount(selectedSelectableIds.length)
            return selectedGroup
        }
        let childrenCount = 0
        let filteredSelected: GroupOfSelectable | null = null

        const filteredGroups = selectedGroup?.children
            ?.map((group) => {
                const filteredChildren =
                    group.children?.filter((child) =>
                        child.name.toLowerCase().includes(filter),
                    ) ?? []
                childrenCount += filteredChildren.length
                return {
                    ...group,
                    totalChildrenCount: filteredChildren.length,
                    children: filteredChildren,
                } as GroupOfSelectable
            })
            .filter(
                (group) =>
                    group.name.toLowerCase().includes(filter) ||
                    group.children?.length != 0,
            )

        filteredSelected = {
            ...selectedGroup,
            children: filteredGroups,
            totalChildrenCount: filteredGroups?.length ?? 0,
        } as GroupOfSelectable

        setFilteredChildrenCount(childrenCount)
        return filteredSelected
    }, [filter, selectableGroup, selectedSelectableIds])

    return (
        <>
            <ViewSettingsComponent
                group={
                    isSelectedShown ? filteredSelectedGroup : selectableGroup
                }
                shouldTreatGroupAsSelectableValue={false}
                shouldExpandGroups={false}
                selectedGroupIds={selectedGroupIds}
                selectedValueIds={selectedSelectableIds}
                getLoadMoreData={async (
                    group: GroupOfSelectable,
                    filter: string,
                ): Promise<void> => {
                    await getLoadMoreData(group, filter, group.children?.length)
                }}
                loadChildrenDataForGroup={loadChildrenDataForGroup}
                getAllValuesInGroup={getAllValuesInGroup}
                onLoadingStateChanged={setIsLoading}
                getFiltered={getFiltered}
                onViewChange={async (
                    isSelectedShown: boolean,
                ): Promise<void> => {
                    setIsSelectedShown(isSelectedShown)
                }}
                onModalOpen={onModalOpen}
                isGrouped={true}
                isSingleSelect={false}
                labels={selectorLabels}
                totalChildrenCount={totalChildrenCount}
                filteredChildrenCount={filteredChildrenCount}
                displayedSelected={null}
                fieldOptions={fieldOptions}
            />
        </>
    )
}
